import {
  MESSAGE_PURPOSE__SIGNUP,
  MESSAGE_PURPOSE__RESET_PASSWORD,
  MESSAGE_PURPOSE__PEP_SIGNUP,
  MESSAGE_PURPOSE__PEP_LOGIN,
  MESSAGE_PURPOSE__PEP_RESET_PASSWORD,
  MESSAGE_PURPOSE__PEP_USERNAME,
  MESSAGE_PURPOSE__PEP_REGISTRATION_CODE,
  MESSAGE_PURPOSE__PEP_RESET_PASSWORD_CODE,
  MESSAGE_PURPOSE__PEP_USERNAME_CODE,
} from '../../common/constants';

/**
 * TECH-2730. Which messages exist, who they reach, and how.
 *
 * Extracted from EmailTemplatesTable so the list, the editor and the coverage
 * test read ONE definition. Until 2026-09-22 this list said six while the
 * server seeded and published nine, and the three extra rows arrived in the
 * browser and were silently dropped.
 */

/**
 * Two audiences, never mixed, each in the order the recipient meets the
 * messages rather than alphabetically. Sorting them together once put
 * "Account enrolment" (staff) above "PEP log in" (patient), so an
 * administrator could not tell at a glance who a message reaches.
 */
export const SECTIONS = [
  {
    key: 'staff',
    purposes: [MESSAGE_PURPOSE__SIGNUP, MESSAGE_PURPOSE__RESET_PASSWORD],
  },
  {
    key: 'pep',
    purposes: [
      MESSAGE_PURPOSE__PEP_SIGNUP,
      MESSAGE_PURPOSE__PEP_LOGIN,
      MESSAGE_PURPOSE__PEP_RESET_PASSWORD,
      MESSAGE_PURPOSE__PEP_USERNAME,
      MESSAGE_PURPOSE__PEP_REGISTRATION_CODE,
      MESSAGE_PURPOSE__PEP_RESET_PASSWORD_CODE,
      MESSAGE_PURPOSE__PEP_USERNAME_CODE,
    ],
  },
];

/**
 * Flattened, for the invariant test that this list stays equal to
 * SYSTEM_MESSAGE_PURPOSES. Derived rather than written out, so the test cannot
 * drift from what the screen actually renders.
 */
export const SECTIONS_PURPOSES = SECTIONS.reduce(
  (acc, section) => acc.concat(section.purposes),
  [],
);

/**
 * Plain-language description of WHO each message reaches and WHEN. English is
 * the defaultValue, not the value: this page ships in seven locales and these
 * were once hardcoded literals, so the purpose column rendered in English
 * everywhere regardless of the chosen language.
 */
const PURPOSE_DEFAULTS = {
  [MESSAGE_PURPOSE__SIGNUP]: 'Account created',
  [MESSAGE_PURPOSE__RESET_PASSWORD]: 'Password reset',
  [MESSAGE_PURPOSE__PEP_SIGNUP]: 'Invitation to sign up',
  [MESSAGE_PURPOSE__PEP_LOGIN]: 'Invitation to log in',
  [MESSAGE_PURPOSE__PEP_RESET_PASSWORD]: 'Password reset',
  [MESSAGE_PURPOSE__PEP_USERNAME]: 'Username reminder',
  [MESSAGE_PURPOSE__PEP_REGISTRATION_CODE]: 'Email verification code',
  [MESSAGE_PURPOSE__PEP_RESET_PASSWORD_CODE]: 'Password reset code',
  [MESSAGE_PURPOSE__PEP_USERNAME_CODE]: 'Username recovery code',
};

export const purposeLabel = (t, purpose) =>
  t(`messagePurpose.${purpose}`, {
    defaultValue: PURPOSE_DEFAULTS[purpose] || purpose,
  });

/**
 * What the accordion row is titled: the organisation's own `name` for this
 * message when it has set one, otherwise the translated purpose label.
 *
 * WHY THIS EXISTS. The editor has always offered a Name field, saved it, and
 * shown it nowhere: the table that displayed it was replaced by this accordion
 * and the header took `purposeLabel` instead. So an administrator could rename
 * a message, see the save succeed, and watch the heading not change. Reported
 * by Tia on 2026-09-24. The field was not broken — it was orphaned.
 *
 * THE TRADE-OFF, WHICH IS REAL AND CHOSEN. `purposeLabel` is translated into
 * seven locales; `name` is ONE string. So an organisation that renames a
 * message gets that one string in every language, and loses the localised
 * heading it had before. That is the same shape as the defect the
 * i18nCoverage guard exists to prevent, and it is accepted here because the
 * alternative — ignoring what an administrator typed and saved — is worse.
 * It degrades only for organisations that choose to rename, only in the
 * heading, and the purpose slug beneath it never moves.
 *
 * A blank or whitespace-only name falls back rather than rendering an empty
 * heading: `''` is what an organisation that has never renamed carries, and
 * an all-spaces name is the same intent typed badly.
 */
export const templateHeading = (t, purpose, template) => {
  const name = template && template.name && template.name.trim();
  return name || purposeLabel(t, purpose);
};

/**
 * Whether a message reaches the recipient by SMS as well as email.
 *
 * Only sendSignUpRequest posts `content`, and only for these two purposes.
 * The other five PEP purposes pass `content: ''` and destructure only subject
 * and htmlContent; the two staff messages have resolveAccountMessage derive
 * their text from htmlContent and never read `content` at all.
 *
 * Shown on the row because an administrator otherwise has no way to know which
 * messages an SMS body is even used for — and it is the same predicate that
 * decides whether the SMS field appears in the editor.
 *
 * ONE definition, re-exported from ./submission.js where the save rules live.
 * Two copies of this predicate would let the field's visibility and the
 * warning that depends on it disagree, which is how a customer ends up
 * authoring SMS copy that is stored and never sent.
 */
export { contentIsSentForPurpose as sendsSms } from './submission';
